// Roster tab presentation. The tab KEYS come from the server navigation document's `rosterTabs`
// (per-subview, — the client holds no role→tab matrix); this only maps a key → its label + whether it
// requires a focused artist, and lays the keys out into the grouped IA. The route for the active scope is
// resolved by the RosterShell layout.

import type { ShellNavItem } from "@cntrl/ui";

/** Human label for a roster tab key. `schedule` forks by subview — see {@link buildRosterNavItems}. */
export function rosterTabLabel(key: string, subview?: string): string {
  switch (key) {
    case "dashboard":
      return "Dashboard";
    case "music":
      // "Streaming", matching the server surface label and the artist shell — the surface is called
      // Streaming everywhere (ART-156). The GROUP that holds it stays "Music" (it also holds Releases).
      return "Streaming";
    case "releases":
      return "Releases";
    case "marketing":
      return "Marketing";
    case "content":
      return "Content";
    case "schedule":
      // The SAME tab key on both lenses; only the word changes. A label org reaches the read-only,
      // money-free variant (no money:* token on its OrgLens capability set), so calling it "Booking"
      // would name a business surface it cannot see. Management/Booking get the full cockpit.
      return subview === "label" ? "Calendar" : "Booking";
    case "calendar":
      // The read-only calendar beside a Booking tab (ART-305) — see buildRosterNavItems.
      return "Calendar";
    case "assets":
      // NOT forked per lens, unlike `schedule` above. Booking's tab opens the SAME Core surface every
      // other lens opens, in its read posture (PO, 2026-09-01 — booking needs the band's press assets,
      // explicitly not Advance Docs). The read posture is what makes the shared label honest: were the
      // tab to open the venue/tech pack instead, calling it "Assets" would name a press-kit surface the
      // seat cannot see, which is exactly the trap the `schedule` fork exists to avoid.
      return "Assets";
    case "antenna":
      return "Antenna";
    case "settings":
      return "Settings";
    default:
      return key;
  }
}

/**
 * The URL SEGMENT for a tab key. Every key is its own segment except `schedule`, which forks — and the fork
 * is the artist shell's, not a new idea: `buildNavItems` there routes the one `schedule` surface to
 * `/booking` or `/calendar` on the same money signal, because "Booking" and "Calendar" are different pages
 * and a URL that names the wrong one is a small lie you have to keep explaining.
 *
 * The KEY never changes — it is server data (`BuildRosterTabs` sends `schedule` to every subview) and the
 * artist-required gate addresses tabs by it (`/roster/g/{key}`). Only the artist-scoped URL differs, which is
 * why this is a mapping and not a rename.
 *
 * {@link rosterTabKeyForSegment} is the inverse, and the pair has to stay one: the shell parses the URL back
 * into a key to light the active tab, so a segment this produces and that cannot read is a nav that goes dark
 * on the page it just opened.
 */
export function rosterTabRoute(key: string, subview?: string): string {
  if (key !== "schedule") return key;
  return subview === "label" ? "calendar" : "booking";
}

/** The inverse of {@link rosterTabRoute} — the URL segment back to the server's tab key. */
export function rosterTabKeyForSegment(segment: string, subview?: string): string {
  if (segment === "booking") return "schedule";
  // On the label lens the calendar IS its `schedule` tab; elsewhere it is the separate Calendar entry.
  if (segment === "calendar") return subview === "label" ? "schedule" : "calendar";
  return segment;
}

/**
 * A SCOPED tab requires a focused artist — every tab EXCEPT the cross-roster Dashboard (which has its own
 * All-roster overview). Selecting a scoped tab with no artist routes to the artist-required gate (the
 * MVP `SCOPED_TABS`, — as data, not CSS-injected).
 */
export function isScopedTab(key: string): boolean {
  return key !== "dashboard";
}


/**
 * The grouped roster IA (nav-parity CR): Dashboard · Music▾ · Content▾ · Touring▾ · Antenna · Settings,
 * mirroring the artist shell's `buildNavItems` so an org seat and a band seat read the same top nav.
 *
 * PURELY PRESENTATIONAL over the server's ordered `rosterTabs` list: a leaf renders iff the server sent
 * its key, and a group with zero surviving children is dropped entirely rather than rendered empty. The
 * client adds nothing — a key absent from the document can never appear here, which is what keeps the
 * "no role→tab matrix on the client" contract true through the regrouping.
 *
 * Two layout facts, both taken from the shipped legacy nav rather than invented:
 * - **Booking nests nothing.** Its three-key set renders Dashboard · Touring▾ · Assets, with Assets a FLAT
 *   leaf — a Content group holding one unrelated child would be a dropdown that exists only to be opened.
 * - **Touring▾ carries one child today.** Still a group, so the shape does not shift when Advance Docs
 *   joins it for the management lens (that needs its own org-lens capability first — the touring tech pack
 *   is crew-internal and label is denied it, so it cannot simply inherit the Assets grant).
 */
export function buildRosterNavItems(tabs: readonly string[], subview?: string): ShellNavItem[] {
  const sent = new Set(tabs);
  const leaf = (key: string) => (sent.has(key) ? { label: rosterTabLabel(key, subview), value: key } : null);
  const present = <T,>(xs: (T | null)[]): T[] => xs.filter((x): x is T => x !== null);

  const items: ShellNavItem[] = [];
  const pushLeaf = (key: string) => {
    const l = leaf(key);
    if (l) items.push({ kind: "leaf", ...l });
  };
  const pushGroup = (label: string, keys: string[]) => {
    const kids = present(keys.map(leaf));
    if (kids.length > 0) items.push({ kind: "group", label, children: kids });
  };

  pushLeaf("dashboard");

  // Where the server's `schedule` tab is the Booking cockpit, the read-only Calendar sits under it (ART-305).
  // It is derived from that same key, so a lens the server sends no `schedule` to gets neither. On the label
  // lens `schedule` already IS the Calendar.
  const touring = sent.has("schedule") && subview !== "label" ? ["schedule", "calendar"] : ["schedule"];
  const leafOrDerived = (key: string) =>
    key === "calendar" ? { label: rosterTabLabel("calendar", subview), value: "calendar" } : leaf(key);
  const pushTouring = () => {
    const kids = present(touring.map(leafOrDerived));
    if (kids.length > 0) items.push({ kind: "group", label: "Touring", children: kids });
  };

  // Booking's set is the tightest and is laid out flat — see the doc comment.
  if (subview === "booking") {
    pushTouring();
    pushLeaf("assets");
    return items;
  }

  pushGroup("Music", ["music", "releases"]);
  pushGroup("Content", ["content", "marketing", "assets"]);
  pushTouring();
  pushLeaf("antenna");
  pushLeaf("settings");
  return items;
}
